// Right-side slide-in drawer that lists every validation error across
// all records after a failed batch save. Click an item → caller decides
// how to focus (switch tab/accordion section, scroll the row into view,
// focus the input).
//
// The panel is purely presentational; the caller owns the error
// aggregation (which records, which fields, which sections) and the
// click handler.

import { useEffect } from 'react'
import { AlertCircle, AlertTriangle, ChevronDown, ChevronRight, X } from 'lucide-react'

import type { GridErrorItem } from './gridTypes'
import { tw } from '../../testHooks'

interface Props {
  open: boolean
  errors: GridErrorItem[]
  title?: string                       // defaults: "Save errors"
  onClose: () => void
  onSelect: (item: GridErrorItem) => void
  // Optional grouping. When false, errors render as a flat list.
  groupByRow?: boolean
}

export default function ErrorPanel({
  open, errors, title = 'Save errors', onClose, onSelect, groupByRow = true,
}: Props) {
  // Esc closes.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  const total = errors.length
  // Group errors by rowKey when requested.
  const grouped = new Map<string, { label: string; items: GridErrorItem[] }>()
  if (groupByRow) {
    for (const e of errors) {
      const key = e.rowKey
      if (!grouped.has(key)) grouped.set(key, { label: e.rowLabel, items: [] })
      grouped.get(key)!.items.push(e)
    }
  }

  return (
    <div
      // Backdrop is transparent — designer wants the drawer alongside
      // the grid so the user can keep working while errors are visible.
      // Click on the drawer itself is intercepted so we don't bubble
      // into row-click handlers.
      onClick={(e) => e.stopPropagation()}
      {...tw('msg', 'error', { panel: 'grid' })}
      style={{
        position: 'absolute',
        top: 0, right: 0, bottom: 0,
        width: 'min(420px, 70vw)',
        background: 'var(--color-bg)',
        color: 'var(--color-text)',
        borderLeft: '1px solid var(--color-border)',
        boxShadow: '-8px 0 24px rgba(0,0,0,0.20)',
        zIndex: 1500,
        display: 'flex', flexDirection: 'column',
        animation: 'slideInRight 0.16s ease-out',
      }}
    >
      <div style={{
        padding: '12px 16px',
        borderBottom: '1px solid var(--color-border)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: 'rgba(239,68,68,0.08)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <AlertCircle size={16} color="#ef4444" />
          <div>
            <strong style={{ fontSize: '0.9225rem' }}>{title}</strong>
            <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
              {total} error{total === 1 ? '' : 's'} · click to fix
            </div>
          </div>
        </div>
        <button
          onClick={onClose}
          {...tw('action', 'close')}
          className="btn btn-ghost btn-sm"
          style={{ padding: 4 }}
          title="Close (Esc)"
        >
          <X size={14} />
        </button>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '6px 0' }}>
        {total === 0 ? (
          <div style={{
            padding: 30, textAlign: 'center',
            color: 'var(--color-text-secondary)', fontSize: '0.8825rem',
          }}>
            No errors.
          </div>
        ) : groupByRow ? (
          Array.from(grouped.entries()).map(([rowKey, group]) => (
            <ErrorGroup
              key={rowKey}
              rowLabel={group.label}
              items={group.items}
              onSelect={onSelect}
            />
          ))
        ) : (
          errors.map((e, i) => (
            <ErrorRow key={`${e.rowKey}::${e.column ?? '__'}::${i}`} item={e} onSelect={onSelect} />
          ))
        )}
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(20px); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
      `}</style>
    </div>
  )
}

function ErrorGroup({
  rowLabel, items, onSelect,
}: {
  rowLabel: string
  items: GridErrorItem[]
  onSelect: (item: GridErrorItem) => void
}) {
  // Start expanded — the user opened the drawer specifically to see
  // these. They can collapse if there are many groups.
  return (
    <details open style={{ borderBottom: '1px solid var(--color-border)' }}>
      <summary style={{
        padding: '6px 12px',
        cursor: 'pointer',
        fontSize: '0.8425rem',
        fontWeight: 600,
        background: 'var(--color-bg-secondary)',
        listStyle: 'none',
        display: 'flex', alignItems: 'center', gap: 6,
      }}>
        <ChevronDown size={11} />
        <span>{rowLabel}</span>
        <span style={{
          marginLeft: 'auto',
          color: '#ef4444', fontWeight: 700, fontSize: '0.8125rem',
        }}>
          {items.length}
        </span>
      </summary>
      {items.map((e, i) => (
        <ErrorRow key={`${e.column ?? '__'}::${i}`} item={e} onSelect={onSelect} />
      ))}
    </details>
  )
}

function ErrorRow({
  item, onSelect,
}: {
  item: GridErrorItem
  onSelect: (item: GridErrorItem) => void
}) {
  return (
    <button
      onClick={() => onSelect(item)}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 8,
        width: '100%', textAlign: 'left',
        padding: '8px 14px 8px 22px',
        background: 'transparent', border: 0,
        borderTop: '1px solid var(--color-border)',
        cursor: 'pointer',
        color: 'var(--color-text)',
        fontSize: '0.8425rem',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-secondary)' }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
    >
      {item.column ? <ChevronRight size={11} style={{ marginTop: 3 }} /> : <AlertTriangle size={11} color="#ef4444" style={{ marginTop: 3 }} />}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {item.columnLabel && (
          <strong style={{ fontSize: '0.8425rem' }}>{item.columnLabel}</strong>
        )}
        <span style={{ color: '#ef4444', lineHeight: 1.4 }}>
          {item.message}
        </span>
        {item.section && (
          <span style={{
            fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
          }}>
            in {item.section}
          </span>
        )}
      </div>
    </button>
  )
}
